import React from 'react';
import { Users } from 'lucide-react';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import EmptyState from '@shared/ui/EmptyState';

export interface PickableWorkspace {
  id: string;
  name: string;
  description?: string | null;
  owner_org_name?: string | null;
}

interface WorkspacePickerListProps {
  workspaces: PickableWorkspace[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  loading?: boolean;
  /** One line above the list saying what picking a workspace will do. */
  prompt?: string;
  /** Right-hand slot on a row — a "Currently tracked" chip, a count, a warning. */
  renderMeta?: (workspace: PickableWorkspace) => React.ReactNode;
}

/**
 * The "which workspaces?" control, shared by every track/add/remove dialog.
 *
 * Each row is a real <label> wrapping its checkbox. The four hand-rolled copies
 * this replaces used a clickable <div> with a checkbox inside, which fired twice
 * on every click — so all four carried three stopPropagation handlers to undo
 * their own markup. A label needs none of that, and it makes the whole row
 * keyboard-reachable through the checkbox instead of being mouse-only.
 */
const WorkspacePickerList: React.FC<WorkspacePickerListProps> = ({
  workspaces,
  selectedIds,
  onToggle,
  loading = false,
  prompt,
  renderMeta,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No workspaces available"
        body="You need access to at least one workspace before you can track anything."
      />
    );
  }

  return (
    <div className="space-y-3">
      {prompt && (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{prompt}</p>
      )}

      {workspaces.map((workspace) => {
        const selected = selectedIds.includes(workspace.id);
        return (
          <label
            key={workspace.id}
            className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-4 transition-colors ${
              selected
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-border-light hover:border-primary-300 hover:bg-background-tertiary dark:border-border-dark dark:hover:border-primary-600 dark:hover:bg-background-dark-tertiary'
            }`}
          >
            <span className="flex min-w-0 items-center gap-3">
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggle(workspace.id)}
                className="shrink-0 rounded border-border-light text-primary-600 focus:ring-primary-500 dark:border-border-dark"
              />
              <span className="min-w-0">
                <span className="block truncate font-medium text-text-primary dark:text-text-dark-primary">
                  {workspace.name}
                </span>
                {workspace.description && (
                  <span className="block truncate text-sm text-text-secondary dark:text-text-dark-secondary">
                    {workspace.description}
                  </span>
                )}
                {workspace.owner_org_name && (
                  <span className="block truncate text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {workspace.owner_org_name}
                  </span>
                )}
              </span>
            </span>
            {renderMeta?.(workspace)}
          </label>
        );
      })}
    </div>
  );
};

export default WorkspacePickerList;
